import React, { useEffect, useState, Fragment } from "react";
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import { Row, Col, CardBody, Card } from "reactstrap";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import DataTable from 'react-data-table-component';
import DiscountCouponService from "../DiscountCoupon/Discountcoupons.service";
import AuthService from "../Authentication/auth.service";
import Alerts from "../../components/Alerts/alerts";
import { Tooltip } from "@mui/material";
import DoctorsListFilter from "../../components/Table Filters/DoctorsListFilter";
import PermissionAccess from '../../PermissionAccess/Access.js';
import EditDoctorModal from './EditDoctorModal';
import { getFileUrl } from "../../utils/fileUtils";
import DoctorICReviewModal from "./DoctorICReviewModal";
import DoctorICHistoryModal from "./DoctorICHistoryModal";
import { SensitiveImage, SensitivePageBanner, SensitiveText } from "../../context/SensitiveInfoContext";
import ImagePreviewModal from "../../components/Modals/ImagePreviewModal";

const formatStatusLabel = (status) => {
  if (!status) return "";
  return String(status)
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join("-");
};

const statusBadgeStyle = (backgroundColor, color = "#fff") => ({
  backgroundColor,
  color,
  fontWeight: 600,
  fontSize: "11px",
  lineHeight: 1.2,
  padding: "6px 10px",
  borderRadius: "4px",
  whiteSpace: "nowrap",
  display: "inline-block",
});

const icStatusBadgeStyle = (status) => {
  switch (status) {
    case "approved":
      return statusBadgeStyle("#1BD35A");
    case "rejected":
      return statusBadgeStyle("#FF0000");
    case "re-submission":
      return statusBadgeStyle("#f7b924", "#222");
    default:
      return statusBadgeStyle("#30b1ff");
  }
};

const tableCustomStyles = {
  table: {
    style: {
      minWidth: "100%",
    },
  },
  headRow: {
    style: {
      minHeight: "46px",
    },
  },
  headCells: {
    style: {
      fontSize: "13px",
      fontWeight: 700,
      paddingLeft: "12px",
      paddingRight: "12px",
      whiteSpace: "nowrap",
    },
  },
  rows: {
    style: {
      minHeight: "58px",
    },
  },
  cells: {
    style: {
      fontSize: "13px",
      paddingLeft: "12px",
      paddingRight: "12px",
      paddingTop: "8px",
      paddingBottom: "8px",
    },
  },
};

const DoctorsList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [discountcoupons, setDiscountcoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterICVerification, setFilterICVerification] = useState('');
  
  const [selectedId, setSelectedId] = useState('');
  const [selectedName, setSelectedName] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [hoveredImage, setHoveredImage] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");

  const [pageLimit, setPageLimit] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const { authData, accessPages, super_admin, current_module } = PermissionAccess();
  const hasPermission = (value) => value === 1 || value === "1" || value === true;
  const canViewDetails =
    super_admin || hasPermission(current_module?.ViewDetails) || hasPermission(current_module?.ViewData);
  const canUpdateAccountStatus =
    super_admin || hasPermission(current_module?.ChangeStatus) || hasPermission(current_module?.EditData);
  const canRegeneratePassword =
    super_admin || hasPermission(current_module?.RegeneratePassword) || hasPermission(current_module?.EditData);
  const canReviewIC = super_admin || hasPermission(current_module?.EditData) || canViewDetails;
  const canDelete = super_admin || hasPermission(current_module?.DeleteData);

  const [showModalEditDoctor, setShowModalEditDoctor] = useState(false);
  const [showICReviewModal, setShowICReviewModal] = useState(false);
  const [showICHistoryModal, setShowICHistoryModal] = useState(false);
  const handleToggleModalEditDoctor = (id) => {
    setShowModalEditDoctor(!showModalEditDoctor);
    setSelectedId(id); // Set the selected ID when toggling modal
  };

  useEffect(() => {
    i18n.changeLanguage(localStorage.getItem('language'));
    fetchDiscountcoupons(0, pageLimit);
  }, [pageLimit]);

  function fetchDiscountcoupons(page_index, page_limit, text, status, type, icVerification) {
    setLoading(true);
    var offset = (page_index - 1) * page_limit;
    if (offset < 1) {
      offset = 0;
    }

    const searchText = text !== undefined ? text : filterText;
    const accountStatus = status !== undefined ? status : filterStatus;
    const accountType = type !== undefined ? type : filterType;
    const icStatus = icVerification !== undefined ? icVerification : filterICVerification;

    DiscountCouponService.getAllDoctors(
      authData.sessionID,
      offset,
      page_limit,
      searchText,
      accountStatus,
      accountType,
      icStatus
    ).then(
      (response) => {
        setLoading(false);
        if (response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
        } else {
          setDiscountcoupons(response.data.data);
          setTotalRows(response.data.count.count);
        }
      },
      (error) => {
        setLoading(false);
        const errContent =
          (error.response && error.response.data) ||
          error.message ||
          error.toString();
        if (errContent) {
          Alerts.swalErrorAlert(errContent);
        } else {
          Alerts.swalErrorAlert(t("Server Error, Please try again later."));
        }
      }
    );
  }

  const handleSearch = (searchText, status, type, icVerification) => {
    setFilterText(searchText);
    setFilterStatus(status);
    setFilterType(type);
    setFilterICVerification(icVerification || "");
    fetchDiscountcoupons(0, pageLimit, searchText, status, type, icVerification || "");
  };

  const handleClear = () => {
    setFilterText('');
    setFilterStatus('');
    setFilterType('');
    setFilterICVerification('');
    fetchDiscountcoupons(0, pageLimit, "", "", "", "");
  };

  const handlePageChange = (page_index) => {
    setCurrentPage(page_index);
    fetchDiscountcoupons(page_index, pageLimit, filterText, filterStatus, filterType, filterICVerification);
  };

  const handlePerRowsChange = async (newPageLimit, page_index) => {
    setPageLimit(newPageLimit);
  };

  const subHeaderComponentMemo = React.useMemo(() => {
    return (
      <DoctorsListFilter
        onSearch={handleSearch}
        onClear={handleClear}
        filterText={filterText}
        filterStatus={filterStatus}
        filterType={filterType}
        filterICVerification={filterICVerification}
      />
    );
  }, [filterText, filterStatus, filterType, filterICVerification]);


  function regeneratePassword(_id) {
    let msg = t("Are you sure you want to regenerate password for this doctor?")
    Alerts.swalConfirmGeneralAlert(msg).then(res => {
      console.log("Resp:-", res)
      if (res === true) {
        sendRegeneratePassword(_id)
      }
    })
  };

  function sendRegeneratePassword(id) {
    setLoading(true);
    DiscountCouponService.regeneratePasswordDoctor(authData.sessionID,id).then((response) => {
      setLoading(false);
      if (response.data.status) {
        if (response.data.message !== '') {
          Alerts.swalSuccessAlert(response.data.message)
        }
      }
      else {
        setLoading(false);
        if (response.data.message !== '') {
          if (response.data.message === "Unauthorized") {
            AuthService.logout();
            navigate("/login");
            window.location.reload();
          }
          else {
            Alerts.swalErrorAlert(response.data.message);
          }
        }
        else {
          Alerts.swalErrorAlert(t("Error: Can't delete the Section."))
        }
      }
    },
      (error) => {
        setLoading(false);
        const errContent =
          (error.response && error.response.data) || error.message || error.toString();
        Alerts.swalErrorAlert(t("Delete operation failed"))
      })
  };

  function confirmDelete(_id) {
    const msg = t("Are you sure you want to delete this doctor?");
    Alerts.swalConfirmDeleteAlert(msg).then((res) => {
      if (res === true) {
        sendDelete(_id);
      }
    });
  }

  function sendDelete(id) {
    setLoading(true);
    DiscountCouponService.deleteDoctor(authData.sessionID, id).then(
      (response) => {
        setLoading(false);
        if (response.data.status) {
          Alerts.swalSuccessAlert(
            response.data.message || t("Doctor deleted successfully.")
          );
          fetchDiscountcoupons(
            currentPage || 0,
            pageLimit,
            filterText,
            filterStatus,
            filterType,
            filterICVerification
          );
        } else if (response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
        } else {
          Alerts.swalErrorAlert(
            response.data.message || t("Error: Can't delete the Doctor.")
          );
        }
      },
      () => {
        setLoading(false);
        Alerts.swalErrorAlert(t("Delete operation failed"));
      }
    );
  }

  function confirmRestore(_id) {
    const msg = t("Are you sure you want to restore this doctor?");
    Alerts.swalConfirmGeneralAlert(msg).then((res) => {
      if (res === true) {
        sendRestore(_id);
      }
    });
  }

  function sendRestore(id) {
    setLoading(true);
    DiscountCouponService.restoreDoctor(authData.sessionID, id).then(
      (response) => {
        setLoading(false);
        if (response.data.status) {
          Alerts.swalSuccessAlert(
            response.data.message || t("Doctor restored successfully.")
          );
          fetchDiscountcoupons(
            currentPage || 0,
            pageLimit,
            filterText,
            filterStatus,
            filterType,
            filterICVerification
          );
        } else if (response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
        } else {
          Alerts.swalErrorAlert(
            response.data.message || t("Error: Can't restore the Doctor.")
          );
        }
      },
      () => {
        setLoading(false);
        Alerts.swalErrorAlert(t("Restore operation failed"));
      }
    );
  }

  const openICReview = (row) => {
    setSelectedId(row._id);
    setSelectedName(row.full_name || "");
    setShowICReviewModal(true);
  };

  const openICHistory = (row) => {
    setSelectedId(row._id);
    setSelectedName(row.full_name || "");
    setShowICHistoryModal(true);
  };


  const mycolumns = [
    {
      name: t('Name'),
      selector: (row) => row.full_name,
      sortable: true,
      grow: 1.2,
      wrap: true,
      minWidth: "140px",
    },
    {
      name: t('Image'),
      width: "90px",
      center: true,
      selector: (row) => {
        if (row.image && row.image !=='test') {
          const imageUrl = getFileUrl(row.image);
          return (
            <SensitiveImage
              src={imageUrl}
              alt={row.full_name}
              style={{ width: "40px", height: "40px", borderRadius: "100%", objectFit: "cover", cursor: "pointer" }}
              onClick={() => {
                setHoveredImage(imageUrl);
                setPreviewTitle(t("Profile Image"));
              }}
            />
          );
        } else {
          return <span className="text-muted">{t('No Image')}</span>;
        }
      },
      sortable: false,
    },
    {
      name: t('Email'),
      selector: (row) =>
        row.status === "Deleted" && row.deleted_email
          ? row.deleted_email
          : row.email,
      sortable: true,
      grow: 1.8,
      wrap: true,
      minWidth: "200px",
    },
    {
      name: t('Type'),
      width: "220px",
      selector: (row) => (
        <span style={row.type === "Doctor" ? statusBadgeStyle("#1BD35A") : statusBadgeStyle("#3f51b5")}>
          {t(row.type)}
        </span>
      ),
      sortable: true,
    },
    {
      name: t('Status'),
      width: "130px",
      selector: (row) => {
        if (row.status === "Deleted") {
          return <span style={statusBadgeStyle("#6c757d")}>{t("Deleted")}</span>;
        }
        const status = formatStatusLabel(row.document_action || "Pending");
        const style =
          status === "Approved"
            ? statusBadgeStyle("#1BD35A")
            : status === "Rejected"
              ? statusBadgeStyle("#FF0000")
              : statusBadgeStyle("#FFD700", "#222");
        return <span style={style}>{status}</span>;
      },
      sortable: true,
    },
    {
      name: t("NRIC"),
      selector: (row) => <SensitiveText value={row.ic_no} />,
      width: "150px",
      wrap: true,
      sortable: false,
    },
    {
      name: t("IC Verification"),
      width: "140px",
      selector: (row) => {
        const status = row.IC_Verification || "pending";
        return (
          <span style={icStatusBadgeStyle(status)}>
            {formatStatusLabel(status)}
          </span>
        );
      },
      sortable: false,
    },
    {
      name: t('Actions'),
      width: "200px",
      ignoreRowClick: true,
      allowOverflow: true,
      selector: (row) => {
        return (
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            {canViewDetails && (
              <Tooltip placement="top" title={`${t("View")} ${t("Details")}`} arrow>
                <NavLink to={`/doctor-details/${row._id}`}>
                  <i className="pe-7s-look text-primary btn-icon-wrapper fs-5 ViewDetails"> </i>
                </NavLink>
              </Tooltip>
            )}

            {row.status === "Deleted" ? (
              canDelete && (
                <Tooltip placement="top" title={t("Restore")} arrow>
                  <i
                    className="pe-7s-back text-success btn-icon-wrapper fs-5"
                    style={{ cursor: "pointer" }}
                    onClick={() => confirmRestore(row._id)}
                  />
                </Tooltip>
              )
            ) : (
              <>
                {canUpdateAccountStatus && (
                  <Tooltip placement="top" title={`${t("Update")} ${t("Account Status")}`} arrow onClick={() => handleToggleModalEditDoctor(row._id)} style={{ cursor: "pointer" }}>
                    <i className="pe-7s-pen btn-icon-wrapper text-primary fs-5 ChangeStatus"> </i>
                  </Tooltip>
                )}

                {canReviewIC && (
                  <Tooltip placement="top" title={t("IC Review")} arrow>
                    <i
                      className="pe-7s-check text-success btn-icon-wrapper fs-5"
                      style={{ cursor: "pointer" }}
                      onClick={() => openICReview(row)}
                    />
                  </Tooltip>
                )}

                {canViewDetails && (
                  <Tooltip placement="top" title={t("View History")} arrow>
                    <i
                      className="pe-7s-clock text-info btn-icon-wrapper fs-5"
                      style={{ cursor: "pointer" }}
                      onClick={() => openICHistory(row)}
                    />
                  </Tooltip>
                )}

                {canRegeneratePassword && (
                  <Tooltip placement="top" title={`${t("Regenerate Password")}`} arrow onClick={() => regeneratePassword(row._id)} style={{ cursor: "pointer" }}>
                    <i className="pe-7s-key text-primary btn-icon-wrapper fs-5 RegeneratePassword" style={{ cursor: "pointer" }}></i>
                  </Tooltip>
                )}

                {canDelete && (
                  <Tooltip placement="top" title={`${t("Delete")} ${t("Doctor")}`} arrow>
                    <i
                      className="pe-7s-trash text-danger btn-icon-wrapper fs-5 DeleteData"
                      style={{ cursor: "pointer" }}
                      onClick={() => confirmDelete(row._id)}
                    />
                  </Tooltip>
                )}
              </>
            )}
          </div>
        );
      }
    },
  ];

  return (
    <Fragment>
      <div className="app-main__outer">
        <div className="app-main__inner">
          <div className="bg-light-gray bg-animation">
            <div className="d-flex justify-content-center align-items-center">
              <Col md="12" className="mx-auto app-login-box">
                <div className="app-page-title">
                  <div className="page-title-wrapper">
                    <div className="page-title-heading fw-bold">
                      <div className="page-title-icon">
                        <i className="pe-7s-news-paper icon-gradient bg-tempting-azure" />
                      </div>
                      <div>
                        {`${t("Service Providers")} - ${t("vSARA")}`}
                        <div className="page-title-subheading">
                          {`${t("Details of")} ${t("Service Provider")}`}
                        </div>
                      </div>
                    </div>
                    <div className="page-title-actions">
                      <DoctorICReviewModal
                        show={showICReviewModal}
                        doctorId={selectedId}
                        onClose={() => setShowICReviewModal(false)}
                        onSuccess={() =>
                          fetchDiscountcoupons(
                            currentPage || 0,
                            pageLimit,
                            filterText,
                            filterStatus,
                            filterType,
                            filterICVerification
                          )
                        }
                      />
                      <DoctorICHistoryModal
                        show={showICHistoryModal}
                        doctorId={selectedId}
                        doctorName={selectedName}
                        onClose={() => setShowICHistoryModal(false)}
                      />
                      <EditDoctorModal
                        onSuccess={fetchDiscountcoupons}
                        showModalEditDoctor={showModalEditDoctor}
                        handleToggleModalEditDoctor={() => handleToggleModalEditDoctor(null)}
                        id={selectedId}
                      />
                    </div>
                  </div>
                </div>
                <Row>
                  <LoadingOverlay tag="div" active={loading} spinner={<DNALoader />}>
                    <Col md="12">
                      <Card className="main-card mb-3">
                        <CardBody>
                          <SensitivePageBanner />
                          <Col md="6" style={{ float: "left", position: "absolute", zIndex: "1" }}>
                            {subHeaderComponentMemo}
                          </Col>
                          <DataTable
                            data={discountcoupons}
                            columns={mycolumns}
                            pagination
                            dense
                            highlightOnHover
                            persistTableHead
                            customStyles={tableCustomStyles}
                            fixedHeader
                            fixedHeaderScrollHeight="400px"
                            subHeader
                            paginationServer
                            paginationTotalRows={totalRows}
                            onChangePage={handlePageChange}
                            onChangeRowsPerPage={handlePerRowsChange}
                            paginationResetDefaultPage={resetPaginationToggle}
                          />
                        </CardBody>
                      </Card>
                    </Col>
                  </LoadingOverlay>
                </Row>
              </Col>
            </div>
          </div>
        </div>
      </div>
      <ImagePreviewModal
        show={!!hoveredImage}
        onClose={() => {
          setHoveredImage(null);
          setPreviewTitle("");
        }}
        src={hoveredImage}
        title={previewTitle}
      />
    </Fragment>
  );
};

export default DoctorsList;
